import * as React from "react";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import {createStaticRanges, DateRangePicker as DateRangePickerInner, RangeKeyDict, StaticRange} from "react-date-range";
import moment from "moment";
import {css} from "@emotion/css";

export interface DateRange {
    startDate: moment.Moment;
    endDate: moment.Moment;
}

interface DateRangePickerProps {
    startDate: moment.Moment;
    endDate: moment.Moment;
    onChange(range: DateRange): void;
    months?: number;
    minDate?: Date;
    maxDate?: Date;
    staticRanges?: StaticRange[];
}

const buildDefaultStaticRanges = (): StaticRange[] => {
    const startOfToday = moment().startOf("day");
    const endOfToday = moment().endOf("day");
    return createStaticRanges([
        {label: "Today", range: () => ({startDate: startOfToday.toDate(), endDate: endOfToday.toDate()})},
        {label: "Yesterday", range: () => ({startDate: startOfToday.clone().subtract(1, "days").toDate(), endDate: endOfToday.clone().subtract(1, "days").toDate()})},
        {label: "Last 7 days", range: () => ({startDate: startOfToday.clone().subtract(7, "days").toDate(), endDate: endOfToday.toDate()})},
        {label: "Last 30 days", range: () => ({startDate: startOfToday.clone().subtract(30, "days").toDate(), endDate: endOfToday.toDate()})},
        {label: "Last 90 days", range: () => ({startDate: startOfToday.clone().subtract(90, "days").toDate(), endDate: endOfToday.toDate()})}
    ]);
};

const DateRangePicker: React.FC<DateRangePickerProps> = (props) => {
    const staticRanges = React.useMemo(() => props.staticRanges ?? buildDefaultStaticRanges(), [props.staticRanges]);

    const startMs = props.startDate.valueOf();
    const endMs = props.endDate.valueOf();
    const ranges = React.useMemo(
        () => [{startDate: new Date(startMs), endDate: new Date(endMs), key: "selection"}],
        [startMs, endMs]
    );

    const handleSelect = (rangesByKey: RangeKeyDict) => {
        const selectedRange = rangesByKey.selection;
        props.onChange({
            startDate: moment(selectedRange.startDate).startOf("day"),
            endDate: moment(selectedRange.endDate).endOf("day"),
        });
    };

    return (
        <div className={datePickerThemeStyle}>
            <DateRangePickerInner
                onChange={handleSelect}
                ranges={ranges}
                staticRanges={staticRanges}
                inputRanges={[]}
                months={props.months ?? 2}
                direction="horizontal"
                fixedHeight
                minDate={props.minDate}
                maxDate={props.maxDate}
            />
        </div>
    );
};

export default DateRangePicker;

const datePickerThemeStyle = css`
    .rdrCalendarWrapper {
        background: var(--colorBackgroundPrimaryDefault);
        color: var(--colorTextPrimary);
    }
    .rdrDateDisplayWrapper {
        background-color: var(--colorBackgroundTertiary);
    }
    .rdrDateDisplayItem {
        background-color: var(--colorBackgroundSecondaryDefault);
        box-shadow: none;
        border-color: var(--colorTextFieldBorderDefault);
    }
    .rdrDateDisplayItem input {
        color: var(--colorTextSecondary);
    }
    .rdrDateDisplayItemActive input {
        color: var(--colorTextPrimary);
    }
    .rdrMonthAndYearPickers select {
        color: var(--colorTextPrimary);
    }
    .rdrNextPrevButton {
        background: var(--colorBackgroundTertiary);
    }
    .rdrNextPrevButton:hover {
        background: var(--colorBackgroundSecondaryDefault);
    }
    .rdrPprevButton i {
        border-color: transparent var(--colorTextPrimary) transparent transparent;
    }
    .rdrNextButton i {
        border-color: transparent transparent transparent var(--colorTextPrimary);
    }
    .rdrWeekDay {
        color: var(--colorTextSecondary);
    }
    .rdrDay {
        color: var(--colorTextPrimary);
    }
    .rdrDayNumber span {
        color: var(--colorTextPrimary);
    }
    .rdrDefinedRangesWrapper {
        background: var(--colorBackgroundPrimaryDefault);
        border-right: solid 1px var(--colorTextFieldBorderDefault);
    }
    .rdrStaticRange {
        background: var(--colorBackgroundPrimaryDefault);
        border-bottom: 1px solid var(--colorTextFieldBorderDefault);
        color: var(--colorTextPrimary);
    }
    .rdrStaticRange:hover .rdrStaticRangeLabel,
    .rdrStaticRange:focus .rdrStaticRangeLabel {
        background: var(--colorBackgroundTertiary);
    }
    .rdrDayPassive .rdrDayNumber span {
        color: var(--colorTextTertiary);
    }
    .rdrDayDisabled {
        background-color: var(--colorBackgroundTertiary);
    }
    .rdrDayDisabled .rdrDayNumber span {
        color: var(--colorTextSecondary);
    }
    .rdrMonthName {
        color: var(--colorTextSecondary);
    }
`;
